import Axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios'
import type {
  PureHttpError,
  RequestMethods,
  PureHttpResponse,
  PureHttpRequestConfig,
} from './types'
import { stringify } from 'qs'
import { Code } from '@/config'
import { useUserStoreHook } from '@/store/modules/user'
import { getToken, formatToken } from '@/utils/auth'
import { initlang } from '@/config/initLang'
import { DeviceUtils } from '@/utils/deviceUtils'
import { PlatformEnum } from '@/types/constantsEnum'
import { CommonUtils } from '@/utils/commonUtils'
import { message } from '@/utils/message'
// 请求根地址
const { VITE_APP_BASE_API } = import.meta.env
export const baseUrlApi = (url: string) => `${VITE_APP_BASE_API}/v1/${url}`
const defaultConfig: AxiosRequestConfig = {
  // 请求超时时间
  timeout: 1000 * 60 * 5,
  headers: {
    Accept: 'application/json, text/plain, */*',
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  // 数组格式参数序列化

  paramsSerializer: function (params) {
    return stringify(params, { arrayFormat: 'repeat' })
  },
}

class PureHttp {
  constructor() {
    this.httpInterceptorsRequest()
    this.httpInterceptorsResponse()
  }

  /** token过期后，暂存待执行的请求 */
  private static requests = []
  /** 初始化配置对象 */
  private static initConfig: PureHttpRequestConfig = {}

  /** 保存当前Axios实例对象 */
  private static axiosInstance: AxiosInstance = axios.create(defaultConfig)

  /** 重连原始请求 */
  private static retryOriginalRequest(config: PureHttpRequestConfig) {
    return new Promise((resolve) => {
      PureHttp.requests.push((token: string) => {
        resolve(config)
      })
    })
  }

  /** 请求拦截 */
  private httpInterceptorsRequest(): void {
    PureHttp.axiosInstance.interceptors.request.use(
      async (config: InternalAxiosRequestConfig): Promise<InternalAxiosRequestConfig> => {
        // 优先判断post/get等方法是否传入回掉，否则执行初始化设置等回掉
        if (typeof config['beforeRequestCallback'] === 'function') {
          config['beforeRequestCallback'](config)
          return config
        }
        if (PureHttp.initConfig.beforeRequestCallback) {
          PureHttp.initConfig.beforeRequestCallback(config)
          return config
        }
        /** 请求白名单，放置一些不需要token的接口（通过设置请求白名单，防止token过期后再请求造成的死循环问题） */
        const whiteList = ['/login']
        const reqPromise = new Promise((resolve) => {
          const data = getToken()
          const locale = initlang()
          // 未登录也要发语言环境
          config.headers['lang'] = locale
          //  当前设备信息
          config.headers['platform'] = DeviceUtils.isMobile()
            ? PlatformEnum.MOBILE
            : PlatformEnum.PC
          if (data) {
            const { token } = data
            if (CommonUtils.getUserInfo()) {
              config.headers['Authorization'] = formatToken(token)
              PureHttp.requests.forEach((cb) => cb(token))
            }
            PureHttp.requests = []
            resolve(config as any)
          } else {
            //
            resolve(config as any)
          }
        })
        return whiteList.some((v) => config.url.indexOf(v) > -1) ? config : config
      },
      (error) => {
        return Promise.reject(error)
      },
    )
  }

  /** 响应拦截 */
  private httpInterceptorsResponse(): void {
    const instance = PureHttp.axiosInstance
    instance.interceptors.response.use(
      (response: PureHttpResponse) => {
        const $config = response.config

        if (typeof $config.beforeResponseCallback === 'function') {
          $config.beforeResponseCallback(response)
          return response.data
        }
        if (PureHttp.initConfig.beforeResponseCallback) {
          PureHttp.initConfig.beforeResponseCallback(response)
          return response.data
        }
        return response.data
      },
      (error: PureHttpError) => {
        const $error = error
        $error.isCancelRequest = Axios.isCancel($error)

        // 所有的响应异常 区分来源为取消请求/非取消请求
        return Promise.reject($error)
      },
    )
  }

  /** 通用请求工具函数 */
  public request<T>(
    method: RequestMethods,
    url: string,
    param?: AxiosRequestConfig,
    axiosConfig?: PureHttpRequestConfig,
  ): Promise<T> {
    if (url.startsWith('draw')) {
      url = baseUrlOtherApi(url)
    } else {
      url = baseUrlApi(url)
    }
    const config = {
      method,
      url,
      ...param,
      ...axiosConfig,
    } as PureHttpRequestConfig

    // 单独处理自定义请求/响应回掉
    return new Promise((resolve, reject) => {
      PureHttp.axiosInstance
        .request(config)
        .then((response: any) => {
          // 请求成功
          if (response.statusCode === Code.SUCCESS.statusCode) {
            resolve(response)
          } else {
            // 登陆失效，token 过期 或者 账户已在其它设备登陆
            if (
              response.statusCode === Code.LOGIN_ERROR.statusCode ||
              response.statusCode === Code.LOGIN_DUPLICATE.statusCode
            ) {
              useUserStoreHook().clearUserInfo()
            }
            message.error(response.message)

            resolve(response)
          }
        })
        .catch((error) => {
          // 全局打印错误信息 非200响应码的错误
          message.error(error)

          console.log(error)

          reject(error)
        })
    })
  }

  /** 单独抽离的post工具函数 */
  public post<T, P>(
    url: string,
    params?: AxiosRequestConfig<T>,
    config?: PureHttpRequestConfig,
  ): Promise<P> {
    return this.request<P>('post', url, params, config)
  }

  /** 单独抽离的get工具函数 */
  public get<T, P>(
    url: string,
    params?: AxiosRequestConfig<T>,
    config?: PureHttpRequestConfig,
  ): Promise<P> {
    return this.request<P>('get', url, params, config)
  }

  /** 单独抽离的patch工具函数 */
  public patch<T, P>(
    url: string,
    params?: AxiosRequestConfig<T>,
    config?: PureHttpRequestConfig,
  ): Promise<P> {
    return this.request<P>('patch', url, params, config)
  }
  /** 单独抽离的put工具函数 */
  public put<T, P>(
    url: string,
    params?: AxiosRequestConfig<T>,
    config?: PureHttpRequestConfig,
  ): Promise<P> {
    return this.request<P>('put', url, params, config)
  }
  /** 单独抽离的delete工具函数 */
  public delete<T, P>(
    url: string,
    params?: AxiosRequestConfig<T>,
    config?: PureHttpRequestConfig,
  ): Promise<P> {
    return this.request<P>('delete', url, params, config)
  }
}

export const http = new PureHttp()
